import { api } from './client';
import type {
  AuditEventDto,
  BootstrapResponse,
  ChangePasswordRequest,
  ConnectionDto,
  ConnectionsGroupedDto,
  CreateGroupRequest,
  CreateUserRequest,
  GroupDto,
  GroupStatusDto,
  NetworkLimitsDto,
  LoginRequest,
  LoginResponse,
  MaintenanceDto,
  MaintenanceScope,
  MotdDto,
  SendCommandRequest,
  ServiceDto,
  ServiceLogDto,
  ServiceMetricsDto,
  SetupFinishRequest,
  SetupState,
  StartServiceRequest,
  TemplateFile,
  TemplateRef,
  TotpConfirmResponse,
  TotpLoginRequest,
  TotpSetupResponse,
  TotpStatus,
  UpdateGroupRequest,
  UpdateRolesRequest,
  UserDto,
  WrapperDto,
} from '@/types/api';

export const authApi = {
  login: (body: LoginRequest) =>
    api.post<LoginResponse>('/api/auth/login', body),
  loginTotp: (body: TotpLoginRequest) =>
    api.post<LoginResponse>('/api/auth/login/totp', body),
  // Phase 18: Logout invalidiert das Token serverseitig (Blacklist)
  logout: () => api.post<{ status: string }>('/api/auth/logout', {}),
};

export const totpApi = {
  status: () => api.post<TotpStatus>('/api/auth/totp/status', {}),
  setup: () => api.post<TotpSetupResponse>('/api/auth/totp/setup', {}),
  confirm: (code: string) =>
    api.post<TotpConfirmResponse>('/api/auth/totp/confirm', { code }),
  disable: (code: string) => api.del<void>(`/api/auth/totp`, undefined, { code }),
};

export const servicesApi = {
  list: (group?: string) =>
    api.get<ServiceDto[]>('/api/services', group ? { group } : undefined),
  get: (name: string) => api.get<ServiceDto>(`/api/services/${name}`),
  start: (body: StartServiceRequest) =>
    api.post<ServiceDto>('/api/services', body),
  stop: (name: string, force = false) =>
    api.del<void>(`/api/services/${name}`, { force }),
  // Phase 20b: Heartbeat-History für Charts
  metrics: (name: string) =>
    api.get<ServiceMetricsDto>(`/api/services/${encodeURIComponent(name)}/metrics`),
  // Schritt 8: Property setzen / löschen (ADMIN). value="" = Delete.
  setProperty: (name: string, key: string, value: string) =>
    api.post<void>(`/api/services/${encodeURIComponent(name)}/properties`,
                   { key, value }),
};

export const groupsApi = {
  list: () => api.get<GroupDto[]>('/api/groups'),
  get: (name: string) =>
    api.get<GroupDto>(`/api/groups/${encodeURIComponent(name)}`),
  // Schritt 9: Live-Status (alive/joinable/util/cooldown + Service-Liste)
  status: (name: string) =>
    api.get<GroupStatusDto>(`/api/groups/${encodeURIComponent(name)}/status`),
  create: (body: CreateGroupRequest) =>
    api.post<GroupDto>('/api/groups', body),
  update: (name: string, body: UpdateGroupRequest) =>
    api.put<GroupDto>(`/api/groups/${encodeURIComponent(name)}`, body),
  remove: (name: string) =>
    api.del<void>(`/api/groups/${encodeURIComponent(name)}`),
};

/** Schritt 10: Network-weiter max-Player-Count. */
export const networkLimitsApi = {
  get: () => api.get<NetworkLimitsDto>('/api/network-limits'),
  set: (maxPlayers: number) =>
    api.put<NetworkLimitsDto>('/api/network-limits', { maxPlayers }),
};

/** Phase 17: Service-Console — letzte Logs + Befehle senden. */
export const consoleApi = {
  tail: (name: string, lines = 200) =>
    api.get<ServiceLogDto[]>(
      `/api/services/${encodeURIComponent(name)}/logs`,
      { tail: lines }
    ),
  sendCommand: (name: string, body: SendCommandRequest) =>
    api.post<{ status: string }>(
      `/api/services/${encodeURIComponent(name)}/command`, body
    ),
};

export const wrappersApi = {
  list: () => api.get<WrapperDto[]>('/api/wrappers'),
};

// Phase 20c: Master-Connections (Wrapper, Proxy, Bridge, Sign)
export const connectionsApi = {
  list: () => api.get<ConnectionDto[]>('/api/connections'),
  grouped: () => api.get<ConnectionsGroupedDto>('/api/connections/grouped'),
};

export const motdApi = {
  get: () => api.get<MotdDto | null>('/api/motd'),
  put: (body: MotdDto) => api.put<MotdDto>('/api/motd', body),
};

export const maintenanceApi = {
  list: () => api.get<MaintenanceDto[]>('/api/maintenance'),
  start: (body: MaintenanceDto) =>
    api.post<MaintenanceDto>('/api/maintenance', body),
  end: (key: string, scope: MaintenanceScope, target?: string | null) =>
    api.del<void>(`/api/maintenance/${key}`, {
      scope,
      target: target ?? undefined,
    }),
};

export const auditApi = {
  list: (filters: {
    domain?: string;
    target?: string;
    since?: string;
    limit?: number;
  } = {}) => api.get<AuditEventDto[]>('/api/audit', filters),
};

export const usersApi = {
  list: () => api.get<UserDto[]>('/api/users'),
  create: (body: CreateUserRequest) => api.post<UserDto>('/api/users', body),
  updateRoles: (name: string, body: UpdateRolesRequest) =>
    api.put<UserDto>(`/api/users/${encodeURIComponent(name)}/roles`, body),
  changePassword: (name: string, body: ChangePasswordRequest) =>
    api.post<{ status: string }>(
      `/api/users/${encodeURIComponent(name)}/password`,
      body
    ),
  delete: (name: string) => api.del<void>(`/api/users/${encodeURIComponent(name)}`),
};

// ============================================================
// Phase 23b: Setup-Wizard, Templates, Wrapper-Bootstrap
// ============================================================

export const setupApi = {
  state: () => api.get<SetupState>('/api/setup/state'),
  finish: (body: SetupFinishRequest) =>
    api.post<{ setupComplete: boolean; adminUsername: string; groupsCreated: number }>(
      '/api/setup/finish', body
    ),
};

export const templatesApi = {
  list: () => api.get<TemplateRef[]>('/api/templates'),
  create: (category: string, variant: string) =>
    api.post<TemplateRef>('/api/templates', { category, variant }),
  delete: (category: string, variant: string) =>
    api.del<void>(
      `/api/templates/${encodeURIComponent(category)}/${encodeURIComponent(variant)}`
    ),
  listFiles: (category: string, variant: string) =>
    api.get<TemplateFile[]>(
      `/api/templates/${encodeURIComponent(category)}/${encodeURIComponent(variant)}/files`
    ),
  readFile: (category: string, variant: string, path: string) =>
    api.get<{ path: string; size: number; contentBase64: string }>(
      `/api/templates/${encodeURIComponent(category)}/${encodeURIComponent(variant)}/file`,
      { path }
    ),
  /**
   * File-Upload braucht multipart/form-data — wir bauen das hier
   * inline statt über api.post (das macht JSON). Token kommt aus dem
   * api-Client; das nutzen wir via fetch() direkt.
   */
  uploadFile: async (category: string, variant: string, path: string, file: File) => {
    const fd = new FormData();
    fd.append('path', path);
    fd.append('file', file);
    const url = `/api/templates/${encodeURIComponent(category)}/${encodeURIComponent(variant)}/file?path=${encodeURIComponent(path)}`;
    const token = localStorage.getItem('jwt');
    const res = await fetch(url, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    });
    if (!res.ok) throw new Error(`Upload fehlgeschlagen: ${res.status}`);
    return res.json() as Promise<{ path: string; size: number }>;
  },
  deleteFile: (category: string, variant: string, path: string) =>
    api.del<void>(
      `/api/templates/${encodeURIComponent(category)}/${encodeURIComponent(variant)}/file`,
      { path }
    ),
};

export const wrapperBootstrapApi = {
  generate: (wrapperId: string, masterHost: string, masterPort: number) =>
    api.post<BootstrapResponse>('/api/wrappers/bootstrap', {
      wrapperId, masterHost, masterPort,
    }),
};

export const autoSetupApi = {
  configs: () =>
    api.post<{ created: string[]; skipped: string[]; notes: string[] }>(
      '/api/templates/auto-setup/configs', {}
    ),
  paperJar: () =>
    api.post<{ version: string; build: number; fileName: string; size: number; path: string }>(
      '/api/templates/auto-setup/paper-jar', {}
    ),
  velocityJar: () =>
    api.post<{ version: string; build: number; fileName: string; size: number; path: string }>(
      '/api/templates/auto-setup/velocity-jar', {}
    ),
};

// Phase 23b: Performance-Charts (Ring-Buffer im Master)
export const metricsHistoryApi = {
  history: (serviceName: string, range: '15m' | '1h' | '6h' | '24h' = '1h') =>
    api.get<{
      service: string;
      range: string;
      points: Array<{
        timestamp: string;
        cpu: number;
        usedMb: number;
        tps: number;
        onlinePlayers: number;
      }>;
    }>(
      `/api/services/${encodeURIComponent(serviceName)}/metrics/history`,
      { range }
    ),
};

// Phase 23b: Backups
export const backupsApi = {
  list: (serviceName: string) =>
    api.get<Array<{
      backupId: string;
      sizeBytes: number;
      success: boolean;
      errorMessage: string;
    }>>(`/api/services/${encodeURIComponent(serviceName)}/backups`),
  trigger: (serviceName: string, label?: string) =>
    api.post<{ status: string; service: string; label: string }>(
      `/api/services/${encodeURIComponent(serviceName)}/backups`,
      label ? { label } : {}
    ),
};
